export type SortValue = string | number | Date | null | undefined;
export type SortDirection = "asc" | "desc";
export type SortState = { key: string; direction: SortDirection } | null;

const rank = (value: SortValue) => (value instanceof Date ? value.getTime() : value);

/** Rows sorted by one column. Empty values always go last; the sort is stable. */
export const sortRows = <T>(
  rows: T[],
  sortValue: ((row: T) => SortValue) | undefined,
  direction: SortDirection
): T[] => {
  if (!sortValue) return rows;
  const sign = direction === "asc" ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, value: rank(sortValue(row)) }))
    .sort((a, b) => {
      const aEmpty = a.value === null || a.value === undefined || a.value === "";
      const bEmpty = b.value === null || b.value === undefined || b.value === "";
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? a.index - b.index : aEmpty ? 1 : -1;
      const order =
        typeof a.value === "string" && typeof b.value === "string"
          ? a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: "base" })
          : (a.value as number) - (b.value as number);
      return order === 0 ? a.index - b.index : order * sign;
    })
    .map(({ row }) => row);
};

/** Clicking a header: ascending, then descending, then back to the original order. */
export const nextSort = (current: SortState, key: string): SortState =>
  current?.key !== key
    ? { key, direction: "asc" }
    : current.direction === "asc"
    ? { key, direction: "desc" }
    : null;

export const pageCount = (total: number, pageSize: number) =>
  Math.max(1, Math.ceil(total / pageSize));

/** "26–50 of 112", or "No rows". */
export const rangeLabel = (page: number, pageSize: number, total: number) => {
  if (total === 0) return "No rows";
  const first = page * pageSize + 1;
  const last = Math.min(total, (page + 1) * pageSize);
  return `${first}–${last} of ${total}`;
};
